// Core imports
import React, { useImperativeHandle, forwardRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Empty, Form, Input, Row, Select, Space, Spin, Tooltip, Typography } from 'antd';
import { CloseOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import EntryPointSelect from './Fields/EntryPointSelect';
import CustomInputNumber from './Fields/CustomInputNumber';
import BotAvatar from '../UI/BotAvatar';

// Misc imports
import { useCreateAutomatedTestsMutation, useUpdateAutomatedTestMutation } from '../../store/automatedTests/automatedTestsApi';
import useTheme from '../../utils/hooks/useTheme';
import { t } from 'i18next';
import { ACTIVITY_TYPES, LANGUAGE_OPTIONS, OPEN_AI_COMPLETION_MODELS } from '../../constants/main';
import is from '../../utils/is';
import { PLUGIN_CATEGORY } from '../../constants/integrations';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';

const AutomatedAgentTestsForm = forwardRef(({ onFormChange = () => {}, activeAutomatedTest }, ref) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();
  const { formProps } = useTheme();
  const selectedLanguage = Form.useWatch('languageCode', form);

  const { data: bots, isLoading: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [createTest, { isLoading: isCreating }] = useCreateAutomatedTestsMutation();
  const [updateTest, { isLoading: isUpdating }] = useUpdateAutomatedTestMutation();
  const [localModels, setLocalModels] = useState([]);

  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [availableTenantLanguages, setAvailableTenantLanguages] = useState([]);
  const allActivityTypes = Object.values(ACTIVITY_TYPES);

  const isPluginActive = useMemo(() => {
    if (activeAutomatedTest?.isCreateMode || !selectedTenantId || !bots?.items) {
      return true;
    }
    const bot = bots.items.find(bot => bot.id === selectedTenantId);
    return bot?.plugins?.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING) ?? false;
  }, [activeAutomatedTest, selectedTenantId, bots]);

  useImperativeHandle(ref, () => ({
    async submit() {
      try {
        const values = await form.validateFields();

        const valuesToSubmit = {
          ...values,
          type: 'DIALOG_TEST',
          profile: {
            ...values.profile,
            model: {
              ...values.profile.model,
              provider: 'openai'
            }
          },
          evaluation: {
            ...values.evaluation,
            model: {
              ...values.evaluation.model,
              provider: 'openai'
            }
          }
        };

        const mutation = activeAutomatedTest.isCreateMode ? createTest : updateTest;
        const payload = { orgId, data: valuesToSubmit };
        if (!activeAutomatedTest.isCreateMode) {
          payload.id = activeAutomatedTest.id;
        }

        const result = await mutation(payload).unwrap();

        onFormChange(false);
        return result;
      } catch (error) {
        console.error('Failed to submit the form:', error);
        return null;
      }
    }
  }));

  useEffect(() => {
    setLocalModels(Object.values(OPEN_AI_COMPLETION_MODELS));
  }, []);

  const updateModels = useCallback((newModelName) => {
    if (newModelName && Object.values(OPEN_AI_COMPLETION_MODELS).filter(model => model === newModelName).length === 0) {
      setLocalModels([...new Set([...Object.values(OPEN_AI_COMPLETION_MODELS), newModelName])]);
    }
  }, []);

  useEffect(() => {
    if (activeAutomatedTest && !activeAutomatedTest.isCreateMode) {
      setSelectedTenantId(activeAutomatedTest.examinedTenantId);
      form.setFieldsValue(activeAutomatedTest);
    } else {
      setSelectedTenantId(null);
      form.resetFields();
    }
    onFormChange(false);
  }, [activeAutomatedTest, form, onFormChange]);

  useEffect(() => {
    if (activeAutomatedTest && !activeAutomatedTest.isCreateMode) {
      form.setFieldsValue(activeAutomatedTest);
    } else {
      form.resetFields();
    }
    onFormChange(false);
  }, [activeAutomatedTest, form, onFormChange]);

  useEffect(() => {
    if(bots?.items?.length > 0 && selectedTenantId) {
      const currentTenant = (bots?.items || []).find(bot => bot?.id === selectedTenantId);

      let availableTenantLanguages = [];
      if(currentTenant) {
        availableTenantLanguages = [currentTenant?.settings?.languages?.primary, ...(currentTenant?.settings?.languages?.additional || [])];

        if(!availableTenantLanguages.includes(selectedLanguage)) {
          form.setFieldValue('languageCode', currentTenant.settings.languages.primary);
        }
      }

      setAvailableTenantLanguages(LANGUAGE_OPTIONS.filter(language => availableTenantLanguages.includes(language.key)));
    }
  }, [selectedTenantId, bots]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...(activeAutomatedTest || {}),
        numOfTrials: activeAutomatedTest?.numOfTrials || 5,
        profile: {
          ...(activeAutomatedTest?.profile || {}),
          constraints: {
            ...(activeAutomatedTest?.profile?.constraints || {}),
            maxNumMessages: activeAutomatedTest?.profile?.constraints?.maxNumMessages || 5
          }
        }
      }}
      onValuesChange={(changedValues, allValues) => {
        if ('examinedTenantId' in changedValues) {
          form.setFieldsValue({ flowEntryPoint: null });
          setSelectedTenantId(changedValues.examinedTenantId);
        }
        onFormChange(true);
      }}
      className="py-4"
      disabled={isCreating || isUpdating || !isPluginActive}
    >
      <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mb-6`}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('General Settings')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Basic information and agent setup for the test.')}
          </Typography.Paragraph>
        </Col>
        <Col xs={formProps.secondaryColSpan}>
          <Form.Item name="name" label="Test Name" rules={[{ required: true, message: 'Test Name field is required' }]}>
            <Input placeholder="Enter test name" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Enter short description" />
          </Form.Item>
          <Form.Item name="examinedTenantId" label="Agent" rules={[{ required: true, message: 'Agent field is required' }]}>
            <Select
              allowClear
              className="fs-base w-100"
              onClear={() => {
                form.resetFields();
              }}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              size="large"
              notFoundContent={
                <Row className="p-4" justify="center" align="middle">
                  {
                    isFetchingBots ? (
                      <Spin />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No agents found')} />
                    )
                  }
                </Row>
              }
              placeholder={t('Select an Agent')}
              showSearch
              filterOption={(input, option) =>
                bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {
                (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING)).map(bot => (
                  <Select.Option key={bot.id} value={bot.id}>
                    <BotAvatar bot={bot} avatarSize={28} />
                  </Select.Option>
                ))
              }
            </Select>
          </Form.Item>
          <Form.Item name="languageCode" label="Language" rules={[{ required: true, message: 'Language field is required' }]}>
            <Select
              showSearch
              allowClear
              filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
              placeholder={<Space size={4}><SearchOutlined className="text-secondary" /> {t('Search available languages')}</Space>}
              options={availableTenantLanguages
                .map(language => ({
                  label: <Space size={4}>{LANGUAGE_OPTIONS.find(lang => lang.key === language.key)?.icon} {language.label}</Space>,
                  value: language.key,
                  searchValue: language.label
                }))
              }
            />
          </Form.Item>
          <Form.Item name="flowEntryPoint" label="Flow" rules={[{ required: true, message: 'Flow field is required' }]}>
            <EntryPointSelect
              primaryLanguage={bots?.items?.find(tenant => tenant.id === selectedTenantId)?.settings?.languages?.primary}
              currentLanguage={selectedLanguage}
              botId={selectedTenantId}
              allowClear={true}
              activityTypesToDisplay={allActivityTypes}
              disabled={!selectedTenantId || !isPluginActive}
            />
          </Form.Item>
          <Form.Item
            name="numOfTrials"
            label="Sessions"
            rules={[{ required: true, message: 'Sessions field is required' }]}
            tooltip={{
              title: (
                <Typography.Text type="secondary">
                  {
                    t('This test runs multiple times, with each run recorded as a separate agent session. This value indicates the total number of sessions to be generated.')
                  }
                </Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <CustomInputNumber
              min={1}
              step={1}
            />
          </Form.Item>
        </Col>

      </Row>
      <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mb-6`}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Synthetic User & Scenario')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Define the synthetic user persona and test case scenario.')}
          </Typography.Paragraph>
        </Col>
        <Col xs={formProps.secondaryColSpan}>
          <Form.Item name={['profile', 'model', 'name']} label="Model" rules={[{ required: true, message: 'Model field is required' }]}>
            <Select
              showSearch
              onSearch={updateModels}
              onBlur={(e) => updateModels(e.target.value)}
              placeholder={ t('Type / Select model')}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              options={(localModels || [])
                .map((element) => {
                  return {
                    value: element,
                    label: element
                  };
                })
              // Keep only unique values
                .filter((elem, index, self) => self.findIndex((t) => {
                  return (t.value === elem.value);
                }) === index)
              }
            />
          </Form.Item>
          <Form.Item
            className="mt-5"
            label={t('Prompt')}
            name={['profile', 'system']}
            rules={[{ required: true, message: 'Prompt field is required' }]}
            tooltip={{
              title: (
                <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <Input.TextArea
              placeholder={t('Enter the prompt')}
              size="large"
              autoSize={true}
              className="resizable-textarea"
            />
          </Form.Item>

        </Col>

      </Row>
      <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mb-6`}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Evaluation Criteria')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Set the rules for how the test results will be evaluated.')}
          </Typography.Paragraph>
        </Col>
        <Col xs={formProps.secondaryColSpan}>
          <Form.Item name={['evaluation', 'model', 'name']} label="Model" rules={[{ required: true, message: 'Model field is required' }]}>
            <Select
              showSearch
              onSearch={updateModels}
              onBlur={(e) => updateModels(e.target.value)}
              placeholder={ t('Type / Select model')}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              options={(localModels || [])
                .map((element) => {
                  return {
                    value: element,
                    label: element
                  };
                })
              // Keep only unique values
                .filter((elem, index, self) => self.findIndex((t) => {
                  return (t.value === elem.value);
                }) === index)
              }
            />
          </Form.Item>
          <Form.Item
            rules={[{ required: true, message: 'Prompt field is required' }]}
            className="mt-5"
            label={t('Prompt')}
            name={['evaluation', 'system']}
            tooltip={{
              title: (
                <Typography.Text type="secondary">{t('The prompt to use when invoking the LLM.')}</Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <Input.TextArea
              placeholder={t('Enter the prompt')}
              size="large"
              autoSize={true}
              className="resizable-textarea"
            />
          </Form.Item>

        </Col>

      </Row>
      <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mb-6`}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Session Settings')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Manage session duration and add tags in generated sessions.')}
          </Typography.Paragraph>
        </Col>
        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            name={['profile', 'constraints', 'maxNumMessages']}
            label="Max Session Turns"
            tooltip={{
              title: (
                <Typography.Text type="secondary">
                  {
                    t('A session turn consists of a user message and the corresponding agent response(s). This value indicates the maximum session turns allowed per session.')
                  }
                </Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            <CustomInputNumber
              min={1}
              step={1}
            />
          </Form.Item>
          <Form.Item
            label={t('Session Tags')}
            tooltip={{
              title: (
                <Typography.Text type="secondary">
                  {
                    t('The tags to be added in the generated sessions to categorize and distinguish them.')
                  }
                </Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
            name="tags"
            className="mb-2"
          >
            <Select
              mode="tags"
              size="large"
              tokenSeparators={[',']}
              placeholder={t('Add session tags')}
              options={
                activeAutomatedTest?.tags?.length > 0 ? activeAutomatedTest?.tags.map((tag)=> {
                  return {
                    value: tag,
                    label: tag
                  };
                }) : null
              }
              onInputKeyDown={(e) => {
                if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
              }}
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  placement="bottomLeft"
                  title={
                    <div className="overflow-y-auto overflow-x-hidden scrollbar-custom" style={{ maxHeight: 280, maxWidth: 220 }}>
                      {
                        omittedValues.map((value, index) => (
                          <Typography.Text.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                            {value.label}

                            <Button
                              size="small"
                              type="link"
                              className="mb-1"
                              icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                            />
                          </Typography.Text.Paragraph>
                        ))
                      }
                    </div>
                  }
                >
                      +{omittedValues.length}
                </Tooltip>
              )}
              // Fix drop-menu position issues by rendering it in the parent element
              getPopupContainer={triggerNode => triggerNode.parentElement}
            />
          </Form.Item>
        </Col>

      </Row>
    </Form>
  );
});

AutomatedAgentTestsForm.propTypes = {
  onFormChange: PropTypes.func,
  activeAutomatedTest: PropTypes.object
};

export default AutomatedAgentTestsForm;
